import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { CheckCircle2, Clock3, ListChecks, RefreshCw, RotateCcw, SkipForward, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { currencyLabel } from "../lib/currencyDisplay";

const queueOptions = [
  ["camera", "Camera"],
  ["media", "Media"],
  ["rpg", "RPG"],
  ["chat", "Chat"],
];

const tabs = [
  ["active", "Active"],
  ["completed", "Completed"],
  ["cancelled", "Cancelled"],
];

export default function Queue() {
  const [tab, setTab] = useState("active");
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/redeem-requests", { params: { status_filter: tab, limit: 100 } });
    setItems(data);
  }, [tab]);

  useEffect(() => {
    load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [load]);

  const skipRequest = async (item) => {
    try {
      await api.post(`/admin/redeem-requests/${item.id}/cancel`, {});
      toast.success(item.cost_amount > 0 ? "Redeem skipped and refunded" : "Redeem skipped");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Skip failed");
    }
  };

  const refundRequest = async (item) => {
    try {
      await api.post(`/admin/redeem-requests/${item.id}/refund`);
      toast.success("Redeem refunded");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refund failed");
    }
  };

  const clearHistory = async () => {
    if (!["completed", "cancelled"].includes(tab)) return;
    if (!window.confirm(`Clear all ${tab} redeem history? This will not change viewer balances.`)) return;
    try {
      const { data } = await api.delete(`/admin/redeem-requests/history/${tab}`);
      toast.success(`Cleared ${data.deleted || 0} ${tab} requests`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Clear history failed");
    }
  };

  const grouped = useMemo(() => {
    const lanes = Object.fromEntries(queueOptions.map(([queue]) => [queue, []]));
    items.forEach((item) => {
      const queue = lanes[item.queue_type] ? item.queue_type : "media";
      lanes[queue].push(item);
    });
    Object.values(lanes).forEach((laneItems) => {
      laneItems.sort((a, b) => {
        if (a.status === "running" && b.status !== "running") return -1;
        if (b.status === "running" && a.status !== "running") return 1;
        if (tab === "active") {
          return (a.queue_position || 999999) - (b.queue_position || 999999) || new Date(a.created_at) - new Date(b.created_at);
        }
        return new Date(b.completed_at || b.cancelled_at || b.created_at) - new Date(a.completed_at || a.cancelled_at || a.created_at);
      });
    });
    return lanes;
  }, [items, tab]);

  return (
    <div className="space-y-6" data-testid="queue-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Viewer interactions</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-[hsl(var(--primary))]" /> Redemption Queue
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Redeemed interactions run automatically in their lane.</p>
        </div>
        <div className="flex gap-1 surface-2 p-1 rounded">
          {tabs.map(([value, label]) => (
            <button key={value} onClick={() => setTab(value)} data-testid={`tab-${value}`} className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded ${tab === value ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--muted-foreground))] hover:text-white"}`}>{label}</button>
          ))}
          <button onClick={load} className="text-xs px-3 py-1.5 text-[hsl(var(--muted-foreground))] hover:text-white" data-testid="refresh-queue"><RefreshCw className="w-3 h-3" /></button>
        </div>
      </div>
      {["completed", "cancelled"].includes(tab) && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={clearHistory}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white"
            data-testid={`clear-${tab}-history`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Clear History
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {queueOptions.map(([queue, label]) => (
          <section key={queue} className="surface overflow-hidden" data-testid={`queue-lane-${queue}`}>
            <div className="px-4 py-3 border-b border-[hsl(var(--border))] flex items-center justify-between">
              <div>
                <h2 className="font-medium">{label}</h2>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{grouped[queue].length} {tab}</div>
              </div>
              <code className="text-xs text-[hsl(var(--muted-foreground))]">{queue}</code>
            </div>
            <div className="divide-y divide-[hsl(var(--border))]">
              {grouped[queue].length === 0 && <div className="p-4 text-sm text-[hsl(var(--muted-foreground))]">No {tab} requests.</div>}
              {grouped[queue].map((item) => (
                <RequestRow key={item.id} item={item} onSkip={skipRequest} onRefund={refundRequest} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function RequestRow({ item, onSkip, onRefund }) {
  const Icon = item.status === "completed" ? CheckCircle2 : item.status === "cancelled" ? XCircle : Clock3;
  const resultCount = (item.action_results || []).length;
  const errorCount = (item.action_results || []).filter((result) => result.status === "error").length;
  const canRefund = item.cost_amount > 0 && item.refunded !== true && ["completed", "cancelled"].includes(item.status);
  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className="w-4 h-4 text-[hsl(var(--primary))] shrink-0" />
            <span className="font-medium truncate">{item.interaction_name}</span>
          </div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
            {item.username} - {item.cost_amount} {currencyLabel({ id: item.cost_type })} - {formatDate(item.created_at)}
          </div>
          {resultCount > 0 && (
            <div className={`text-xs mt-1 ${errorCount ? "text-[hsl(var(--destructive))]" : "text-emerald-300"}`}>
              {resultCount} action results{errorCount ? `, ${errorCount} errors` : ""}
            </div>
          )}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-2">
          <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{item.status}</span>
          {item.status === "pending" && (
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => onSkip(item)}
                className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white"
                data-testid={`skip-request-${item.id}`}
              >
                <SkipForward className="w-3.5 h-3.5" /> Skip
              </button>
            </div>
          )}
          {canRefund && (
            <button
              type="button"
              onClick={() => onRefund(item)}
              className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
              data-testid={`refund-request-${item.id}`}
            >
              <RotateCcw className="w-3.5 h-3.5" /> Refund
            </button>
          )}
          {item.refunded === true && <span className="text-xs text-emerald-300">Refunded</span>}
        </div>
      </div>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch (err) {
    return value;
  }
}
